import { History } from 'history'
import { get, has } from 'lodash'
import {
  COUNTRY_SHORTCODES,
  PR_MARKET_ID,
  SupplyAcquisitionMethods,
} from '../../constants'
import * as Exp from '../../experimentConstants'
import routes from '../../routes'
import { getOnboardingConfig } from '../onboardingConfig'
import { isCaviarDomain } from '../window'
import { determineCurrentStepAustralia } from './utils/determine-current-step/australia'
import { determineCurrentStepJapan } from './utils/determine-current-step/japan'
import { determineCurrentStepNewZealand } from './utils/determine-current-step/newZealand'
import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'

// <Step> : <path param>
export const enum Steps {
  PhoneVerification = 'verify',
  ProfileSetup = 'profile',
  ApplicationClosed = 'applicationclosed',
  BackgroundCheck = 'backgroundcheck',
  BackgroundCheckAwait = 'backgroundcheckawait',
  BackgroundCheckExternalLink = 'backgroundcheck-external-link',
  GetSupplies = 'getsupplies',
  GetSuppliesV2 = 'getsuppliesv2',
  SignW9 = 'signw9',
  SignW9V2 = 'signw9v2',
  MailingAddress = 'mailingaddress',
  MailingAddressV2 = 'mailingaddressv2',
  Orientation = 'orientation',
  DropshippingConfirm = 'dropshipping',
  IdentityVerificationAwait = 'identityverificationawait',
  VehicleDetails = 'vehicledetails',
  SignUpCompletion = 'signupcompletion',
  StartOnBoardingWorkflow = 'startOnBoardingWorkflow',
  ProgressionCheck = 'progressioncheck',
  JapanNonCitizenGuide = 'japanNonCitizenGuide',
  Reschedule = 'reschedule',
}

export interface StepItem {
  path: Steps
  includeMobileHeader?: boolean
}

export type stepType = {
  [x: string]: StepItem
}

// STEPS configuration { path, includeHeader, isDSJ }
export const STEPS: stepType = {
  [Steps.PhoneVerification]: {
    path: Steps.PhoneVerification,
  },
  [Steps.ProfileSetup]: {
    path: Steps.ProfileSetup,
  },
  [Steps.ApplicationClosed]: {
    path: Steps.ApplicationClosed,
  },
  [Steps.BackgroundCheck]: {
    path: Steps.BackgroundCheck,
  },
  [Steps.BackgroundCheckExternalLink]: {
    path: Steps.BackgroundCheckExternalLink,
  },
  [Steps.BackgroundCheckAwait]: {
    path: Steps.BackgroundCheckAwait,
  },
  [Steps.GetSupplies]: {
    path: Steps.GetSupplies,
  },
  [Steps.GetSuppliesV2]: {
    path: Steps.GetSuppliesV2,
  },
  [Steps.SignW9]: {
    path: Steps.SignW9,
  },
  [Steps.SignW9V2]: {
    path: Steps.SignW9V2,
    includeMobileHeader: false,
  },
  [Steps.MailingAddress]: {
    path: Steps.MailingAddress,
  },
  [Steps.MailingAddressV2]: {
    path: Steps.MailingAddressV2,
    includeMobileHeader: false,
  },
  [Steps.Orientation]: {
    path: Steps.Orientation,
  },
  [Steps.DropshippingConfirm]: {
    path: Steps.DropshippingConfirm,
  },
  [Steps.IdentityVerificationAwait]: {
    path: Steps.IdentityVerificationAwait,
  },
  [Steps.VehicleDetails]: {
    path: Steps.VehicleDetails,
  },
  [Steps.SignUpCompletion]: {
    path: Steps.SignUpCompletion,
  },
  [Steps.StartOnBoardingWorkflow]: {
    path: Steps.StartOnBoardingWorkflow,
  },
  [Steps.ProgressionCheck]: {
    path: Steps.ProgressionCheck,
  },
  [Steps.JapanNonCitizenGuide]: {
    path: Steps.JapanNonCitizenGuide,
  },
  [Steps.Reschedule]: {
    path: Steps.Reschedule,
  },
}

const defaultStep = Steps.ProfileSetup

// get matching step, or default
export const stepMatch = step =>
  step in STEPS ? STEPS[step] : STEPS[defaultStep]

/**
 * Adds an entry to the browser's session history stack,
 * which will trigger a route based navigation, without
 * a document reload
 *
 * Warning: because the redux store from DSJ hydrates on
 * document refreshes, simply using push may lead to
 * dependent data not being properly populated
 */
export const push = (
  uniqueLink: string,
  step: Steps,
  history: History
): void => {
  const { path } = STEPS[step]
  const pathname = routes.dasherflow.buildPath({
    uniqueLink,
    step: path,
  })
  history.push(pathname)
}

/**
 * Causes the window to load and display the document,
 * based on the uniqueLink and the steps.
 *
 * Using this over the `push` method will diminish user experience,
 * but will ensure that the redux store will be properly populated.
 */
export const pageLoad = (uniqueLink: string, step: Steps): void => {
  const { path } = STEPS[step]
  let pathname = routes.dasherflow.buildPath({
    uniqueLink,
    step: path,
  })

  // if we're on caviar branded landing page, use a full reference
  if (isCaviarDomain()) {
    pathname = `https://www.doordash.com${pathname}`
  }

  window.location.assign(pathname)
}

export enum BGCResult {
  consider = 'consider',
  suspended = 'suspended',
}

export enum IDVStatus {
  NOT_COMPLETED = 'unspecified',
  SUCCESS = 'success',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CREATED = 'created',
  NEEDS_REVIEW = 'needs_review',
  APPROVED = 'approved',
  DECLINED = 'declined',
  EXPIRED = 'expired',
  PENDING = 'pending',
  NOT_CREATED = '',
}

export const successIdvStatuses: IDVStatus[] = [
  IDVStatus.SUCCESS,
  IDVStatus.APPROVED,
]

export const idvAwaitStatusList = [
  IDVStatus.FAILED,
  IDVStatus.NEEDS_REVIEW,
  IDVStatus.DECLINED,
]
export const idvSuccessStatusList = [IDVStatus.COMPLETED, IDVStatus.APPROVED]

/**
 * determineCurrentStep
 *
 * @param application
 * @param experiments
 * @param uniqueLink
 * @param vargs [var args, arg1, arg2, ...]
 */
export const determineCurrentStep = (
  application,
  experiments,
  uniqueLink,
  {
    isRedirectToProgressionPage = true,
    supplyAcquisitionMethod,
    mailingAddressSubmitted,
    isAtCapacity,
    idvStatus,
    hasCompletedFirstDelivery,
    step = null,
    isDxOnboardingPlatformWorkflowComplete,
  }
) => {
  const dropshippingAB = get(experiments, 'dropshipping_ab', false)
  const dropshipping = get(experiments, 'dropshipping', true)
  const dropshippingSkipSelection = get(
    experiments,
    'dropshipping_skip_selection',
    false
  )
  const uiRefresh = get(experiments, 'dasher_signup_ui_refresh', false)
  const isInAddressCollectionAfterDashExperiment = get(
    experiments,
    Exp.ADDRESS_COLLECTION_AFTER_DASH,
    false
  )
  const isInInstantDashing = get(experiments, Exp.INSTANT_DASHING_V2, false)
  const phoneVerification = get(
    experiments,
    Exp.DX_PHONE_NUMBER_VERIFICATION_SIGNUP,
    false
  )

  const country = get(application, 'country', '')
  const isAU = country === COUNTRY_SHORTCODES.AU
  const isNZ = country === COUNTRY_SHORTCODES.NZ
  const isDE = country === COUNTRY_SHORTCODES.DE
  const isJP = country === COUNTRY_SHORTCODES.JP
  const isUS = country === COUNTRY_SHORTCODES.US
  const isCA = country === COUNTRY_SHORTCODES.CA

  const isGermanyOnboardingV2Enabled =
    isDE && get(experiments, Exp.GERMANY_DX_ONBOARDING_V2, false)
  const isDxOnboardingV2Enabled = get(experiments, 'dx_onboarding_v2', false)

  if (application) {
    if (isCA) {
      return determineCurrentStepCanada(application, experiments, uniqueLink, {
        supplyAcquisitionMethod,
        mailingAddressSubmitted,
        isAtCapacity,
      })
    }

    if (isJP) {
      return determineCurrentStepJapan(application, {
        experiments,
        country,
        isRedirectToProgressionPage,
        isAtCapacity,
        hasCompletedFirstDelivery,
        step,
        isDxOnboardingPlatformWorkflowComplete,
      })
    }

    if (isAU) {
      return determineCurrentStepAustralia(application, {
        isAtCapacity,
        isDxOnboardingPlatformWorkflowComplete,
      })
    }

    if (isNZ && isDxOnboardingV2Enabled) {
      return determineCurrentStepNewZealand(
        isDxOnboardingPlatformWorkflowComplete
      )
    }

    if (get(application, 'market', 0) === PR_MARKET_ID) {
      return determineCurrentStepPuertoRico(
        application,
        experiments,
        uniqueLink,
        { isAtCapacity }
      )
    }

    /**
     * if country is Germany and GERMANY_DX_ONBOARDING_V2 experiment
     * is enabled, trigger the config driven UI for onboarding
     */
    if (isGermanyOnboardingV2Enabled) {
      return STEPS[Steps.StartOnBoardingWorkflow]
    }

    if (
      isUS &&
      (idvAwaitStatusList.includes(application.identityVerificationStatus) ||
        idvAwaitStatusList.includes(idvStatus.value))
    ) {
      return STEPS[Steps.IdentityVerificationAwait]
    }

    if (
      hasCompletedFirstDelivery ||
      mailingAddressSubmitted ||
      application.w9Signed
    ) {
      return STEPS[Steps.DropshippingConfirm]
    }

    if (
      phoneVerification &&
      has(application, 'isPhoneNumberVerified') &&
      application.isPhoneNumberVerified === false
    ) {
      return STEPS[Steps.PhoneVerification]
    }

    if (
      has(application, 'completedProfileStep') &&
      application.completedProfileStep === false
    ) {
      return STEPS[Steps.ProfileSetup]
    }

    const hasDasher = !!application.dasherId
    const hasDob = !!application.dateOfBirth
    const needsBgCheckInfo = !!get(application, 'needsBgCheckInfo', null)
    const isBackgroundCheckRequired = getOnboardingConfig(
      'backgroundCheck',
      'required',
      country
    )

    if (isBackgroundCheckRequired) {
      // More historical context regarding why dasher or DoB is not set
      // https://github.com/doordash/web/pull/5164
      if (needsBgCheckInfo || !hasDasher || !hasDob) {
        if (isAtCapacity) {
          return STEPS[Steps.ApplicationClosed]
        }
        if (
          isUS &&
          get(experiments, Exp.DX_BGC_ONBOARDING_PLATFORM, false) &&
          idvSuccessStatusList.includes(application.identityVerificationStatus)
        ) {
          return STEPS[Steps.StartOnBoardingWorkflow]
        }
        return STEPS[Steps.BackgroundCheck]
      }
    }

    const showVehicleDetailsStep = getOnboardingConfig(
      'vehicleDetails',
      'showThisStep',
      country
    )
    if (showVehicleDetailsStep && !hasDasher) {
      if (isAtCapacity) {
        return STEPS[Steps.ApplicationClosed]
      }
      return STEPS[Steps.VehicleDetails]
    }

    const showOrientationStep = getOnboardingConfig(
      'orientation',
      'showThisStep',
      country
    )
    const hasScheduledOrientation = Boolean(application.dasherOrientation)

    if (showOrientationStep && !hasScheduledOrientation) {
      return STEPS[Steps.Orientation]
    }

    if (
      has(application, 'dasherOrientation') &&
      application.dasherOrientation === null &&
      dropshippingAB === false &&
      dropshipping === true
    ) {
      if (mailingAddressSubmitted) {
        return uiRefresh === true ? STEPS[Steps.SignW9V2] : STEPS[Steps.SignW9]
      }

      if (isInAddressCollectionAfterDashExperiment && isInInstantDashing) {
        if (isUS && get(experiments, Exp.DX_BGC_ONBOARDING_PLATFORM, false)) {
          return STEPS[Steps.StartOnBoardingWorkflow]
        }
        return STEPS[Steps.DropshippingConfirm]
      }

      if (
        dropshippingSkipSelection === true ||
        supplyAcquisitionMethod === SupplyAcquisitionMethods.DROPSHIPPING
      ) {
        return uiRefresh === true
          ? STEPS[Steps.MailingAddressV2]
          : STEPS[Steps.MailingAddress]
      }
      return uiRefresh === true
        ? STEPS[Steps.GetSuppliesV2]
        : STEPS[Steps.GetSupplies]
    }

    const showSignUpCompletionStep = getOnboardingConfig(
      'signUpCompletion',
      'showThisStep',
      country
    )

    if (showSignUpCompletionStep && hasDasher) {
      return STEPS[Steps.SignUpCompletion]
    }
  }
  return false
}

const determineCurrentStepPuertoRico = (
  application,
  experiments,
  _,
  { isAtCapacity }
) => {
  const scheduledOrientation = get(application, 'orientationDate', null) != null
  const phoneVerification = get(
    experiments,
    Exp.DX_PHONE_NUMBER_VERIFICATION_SIGNUP,
    false
  )
  const completedProfileStep = get(application, 'completedProfileStep', false)
  const completedBackgroundStep =
    get(application, 'dateOfBirth') != null &&
    get(application, 'vehicleType') != null

  if (
    scheduledOrientation ||
    (completedProfileStep && completedBackgroundStep)
  ) {
    return STEPS[Steps.Orientation]
  }

  if (completedProfileStep) {
    if (isAtCapacity) {
      return STEPS[Steps.ApplicationClosed]
    }
    return STEPS[Steps.BackgroundCheck]
  } else if (
    phoneVerification &&
    has(application, 'isPhoneNumberVerified') &&
    application.isPhoneNumberVerified === false
  ) {
    return STEPS[Steps.PhoneVerification]
  }

  return STEPS[Steps.ProfileSetup]
}

const determineCurrentStepCanada = (
  application,
  experiments,
  uniqueLink,
  { supplyAcquisitionMethod, mailingAddressSubmitted, isAtCapacity }
) => {
  const caDropshippingEnabled = get(
    experiments,
    'canada_dropshipping_embed_checkr_flow',
    false
  )
  const dropshippingSkipSelection = get(
    experiments,
    'dropshipping_skip_selection',
    false
  )

  // useDropshipping is true once an applicant
  // has submitted address for dropshipping
  const submittedDropshipping = get(application, 'useDropshipping', false)
  // useOrientation is true once an applicant
  // has selected an in-person orientation
  const scheduledOrientation = get(application, 'orientationDate', null) != null
  const phoneVerification = get(
    experiments,
    Exp.DX_PHONE_NUMBER_VERIFICATION_SIGNUP,
    false
  )
  const completedProfileStep = get(application, 'completedProfileStep', false)
  const completedBackgroundStep =
    get(application, 'dateOfBirth') != null &&
    get(application, 'vehicleType') != null

  if (isFeatureFlagEnabled(Exp.DX_APP_DASHER_ONBOARDING_REDIRECT) && completedProfileStep) {
    window.location.replace(`/dasher/onboarding?applicantUniqueLink=${uniqueLink}`)
    return false
  }

  // show dropship confirm if mailing address submitted
  // (coincides with useDropshipping=true on backend)
  if (submittedDropshipping && mailingAddressSubmitted) {
    // until background check is complete, we want to show
    // mailing address page with checkr modal
    if (application.backgroundCheckResults === 'incomplete') {
      return STEPS[Steps.MailingAddress]
    }
    return STEPS[Steps.DropshippingConfirm]
  }

  // show orientation confirm if orientation scheduled
  if (scheduledOrientation) {
    return STEPS[Steps.Orientation]
  }
  if (completedProfileStep && completedBackgroundStep) {
    if (!caDropshippingEnabled) {
      return STEPS[Steps.Orientation]
    }
    if (
      dropshippingSkipSelection ||
      supplyAcquisitionMethod === SupplyAcquisitionMethods.DROPSHIPPING
    ) {
      return STEPS[Steps.MailingAddress]
    }

    if (supplyAcquisitionMethod === SupplyAcquisitionMethods.GO_INTO_OFFICE) {
      return STEPS[Steps.Orientation]
    }

    return STEPS[Steps.GetSupplies]
  }

  if (completedProfileStep) {
    if (isAtCapacity) {
      return STEPS[Steps.ApplicationClosed]
    }
    return STEPS[Steps.BackgroundCheck]
  } else if (
    phoneVerification &&
    has(application, 'isPhoneNumberVerified') &&
    application.isPhoneNumberVerified === false
  ) {
    return STEPS[Steps.PhoneVerification]
  }

  return STEPS[Steps.ProfileSetup]
}
